/**
 * Category-aware depreciation defaults for the public ACV vs RCV calculator.
 *
 * Rates derived from publicly documented insurance industry tables
 * (Nationwide / ClaimsPages / ACV worksheets used by major carriers). Educational
 * modeling only; not for adjusting real claims.
 *
 * `curve` controls the depreciation shape:
 *   - "straight":    linear age/lifespan, capped at 90% (10% RCV floor)
 *   - "accelerated": (age/lifespan)^0.7 — front-loaded loss, capped at 95%
 *   - "flat":        no depreciation (jewelry, fine art, collectibles)
 */

export type DepreciationCurve = "straight" | "accelerated" | "flat";

export interface CalcCategory {
  id: string;
  label: string;
  /** Useful life in years (industry default) */
  lifespan: number;
  curve: DepreciationCurve;
  hint?: string;
}

export const CALC_CATEGORIES: CalcCategory[] = [
  // Furniture
  { id: "sofa", label: "Sofa / Sectional", lifespan: 10, curve: "straight" },
  { id: "bedroom_set", label: "Bedroom set", lifespan: 15, curve: "straight" },
  { id: "dining_set", label: "Dining table & chairs", lifespan: 15, curve: "straight" },
  { id: "mattress", label: "Mattress", lifespan: 8, curve: "straight" },
  { id: "rug", label: "Area rug", lifespan: 10, curve: "straight" },
  { id: "office_furniture", label: "Office furniture", lifespan: 10, curve: "straight" },

  // Electronics (accelerated)
  { id: "tv", label: "TV", lifespan: 7, curve: "accelerated", hint: "Loses value fast" },
  { id: "laptop", label: "Laptop", lifespan: 5, curve: "accelerated", hint: "Loses value fast" },
  { id: "desktop", label: "Desktop computer", lifespan: 6, curve: "accelerated" },
  { id: "phone", label: "Smartphone", lifespan: 4, curve: "accelerated" },
  { id: "tablet", label: "Tablet", lifespan: 5, curve: "accelerated" },
  { id: "audio", label: "Audio / speakers", lifespan: 8, curve: "accelerated" },
  { id: "camera", label: "Camera", lifespan: 7, curve: "accelerated" },
  { id: "console", label: "Game console", lifespan: 7, curve: "accelerated" },

  // Major appliances
  { id: "refrigerator", label: "Refrigerator", lifespan: 14, curve: "straight" },
  { id: "washer_dryer", label: "Washer / Dryer", lifespan: 12, curve: "straight" },
  { id: "dishwasher", label: "Dishwasher", lifespan: 10, curve: "straight" },
  { id: "range", label: "Range / Oven", lifespan: 15, curve: "straight" },
  { id: "microwave", label: "Microwave", lifespan: 9, curve: "straight" },

  // Small appliances & kitchen
  { id: "small_appliance", label: "Small kitchen appliance", lifespan: 8, curve: "straight" },
  { id: "cookware", label: "Cookware / kitchenware", lifespan: 10, curve: "straight" },

  // Apparel
  { id: "clothing_adult", label: "Clothing (adult)", lifespan: 5, curve: "straight" },
  { id: "clothing_kids", label: "Clothing (kids)", lifespan: 3, curve: "straight" },
  { id: "shoes", label: "Shoes", lifespan: 3, curve: "straight" },

  // Tools / outdoor
  { id: "power_tools", label: "Power tools", lifespan: 15, curve: "straight" },
  { id: "hand_tools", label: "Hand tools", lifespan: 20, curve: "straight" },
  { id: "lawn_equipment", label: "Lawn equipment", lifespan: 10, curve: "straight" },

  // Sports / recreation
  { id: "bicycle", label: "Bicycle", lifespan: 10, curve: "straight" },
  { id: "sports_gear", label: "Sports equipment", lifespan: 7, curve: "straight" },

  // Kids
  { id: "toys", label: "Toys / kids' gear", lifespan: 5, curve: "straight" },

  // Flat (no depreciation)
  { id: "jewelry", label: "Jewelry", lifespan: 25, curve: "flat", hint: "Typically scheduled, no depreciation" },
  { id: "fine_art", label: "Fine art", lifespan: 50, curve: "flat", hint: "No depreciation in most policies" },
  { id: "collectibles", label: "Collectibles", lifespan: 50, curve: "flat" },

  // Misc
  { id: "books", label: "Books / media", lifespan: 10, curve: "straight" },
  { id: "luggage", label: "Luggage", lifespan: 8, curve: "straight" },
  { id: "musical_instrument", label: "Musical instrument", lifespan: 20, curve: "straight" },
  { id: "other", label: "Other / general", lifespan: 8, curve: "straight" },
];

export const CATEGORY_BY_ID: Record<string, CalcCategory> = Object.fromEntries(
  CALC_CATEGORIES.map((c) => [c.id, c]),
);

export function getCategory(id: string): CalcCategory {
  return CATEGORY_BY_ID[id] ?? CATEGORY_BY_ID.other;
}
